import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface TableState<T = any> {
    [tableId: string]: {
        selectedRows: [
            {
                page: number;
                data: [];
            }
        ];
        selectedRowKeys: [
            {
                page: number;
                data: number[];
            }
        ];
        tableData?: T[];
        totalRecords?: number;
        filter?: any;
        unselectedRowsKeys: Array<number | string>;
        allSelected: boolean;

        loading?: boolean;
        refreshKey: number;
        totalData?: T[];
        extras?: {
            remainingSumInsured?: number;
        };
    };
}

const initialState: TableState = {
    default: {
        selectedRows: [{ page: 0, data: [] }],
        selectedRowKeys: [{ page: 0, data: [] }],
        tableData: [],
        totalRecords: 0,
        filter: {},
        unselectedRowsKeys: [],

        loading: false,
        refreshKey: 0,
        totalData: [],
        extras: {},
        allSelected: false,
    },
};

const initializeTableState = (
    state: TableState,
    tableId: string = "default"
) => {
    if (!state[tableId]) {
        state[tableId] = {
            selectedRows: [{ page: 0, data: [] }],
            selectedRowKeys: [{ page: 0, data: [] }],
            tableData: [],
            unselectedRowsKeys: [],
            allSelected: false,
            totalRecords: 0,
            filter: {},
            loading: false,
            refreshKey: 0,
            totalData: [],
            extras: {},
        };
    }
};

const tableSlice = createSlice({
    name: "tableStore",
    initialState,
    reducers: {
        updateSelectedRows: (
            state: TableState,
            action: PayloadAction<{ tableId?: string; page: number; data: [] }>
        ) => {
            const { tableId = "default", page, data } = action.payload;
            initializeTableState(state, tableId);
            const pageIndex = state[tableId].selectedRows.findIndex(
                (row) => row.page === page
            );
            if (pageIndex !== -1) {
                state[tableId].selectedRows[pageIndex].data = data;
            } else {
                state[tableId].selectedRows.push({ page, data });
            }
        },
        resetSelectedRows: (
            state: TableState,
            action: PayloadAction<{ tableId?: string }>
        ) => {
            const { tableId = "default" } = action.payload;
            if (state[tableId]) {
                state[tableId].selectedRows = [{ page: 0, data: [] }];
            }
        },
        updateUnselectedRowsKeys: (
            state: TableState,
            action: PayloadAction<{
                tableId?: string;
                keys: Array<number | string>;
            }>
        ) => {
            const { tableId = "default", keys } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].unselectedRowsKeys = keys;
        },
        resetUnselectedRowsKeys: (
            state: TableState,
            action: PayloadAction<{ tableId?: string }>
        ) => {
            const { tableId = "default" } = action.payload;
            if (state[tableId]) {
                state[tableId].unselectedRowsKeys = [];
            }
        },
        setAllSelected: (
            state: TableState,
            action: PayloadAction<{ tableId?: string; allSelected: boolean }>
        ) => {
            const { tableId = "default", allSelected } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].allSelected = allSelected;
        },
        updateSelectedRowKeys: (
            state: TableState,
            action: PayloadAction<{
                tableId?: string;
                page: number;
                data: number[];
            }>
        ) => {
            const { tableId = "default", page, data } = action.payload;
            initializeTableState(state, tableId);
            const pageIndex = state[tableId].selectedRowKeys.findIndex(
                (row) => row.page === page
            );
            if (pageIndex !== -1) {
                state[tableId].selectedRowKeys[pageIndex].data = data;
            } else {
                state[tableId].selectedRowKeys.push({ page, data });
            }
        },
        resetSelectedRowsKeys: (
            state: TableState,
            action: PayloadAction<{ tableId?: string }>
        ) => {
            const { tableId = "default" } = action.payload;
            if (state[tableId]) {
                state[tableId].selectedRowKeys = [{ page: 0, data: [] }];
            }
        },
        updateTableData: <T = any>(
            state: TableState,
            action: PayloadAction<{ tableId?: string; data: T[] }>
        ) => {
            const { tableId = "default", data } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].tableData = data;
        },
        updateTotalRecords: (
            state: TableState,
            action: PayloadAction<{ tableId?: string; total: number }>
        ) => {
            const { tableId = "default", total } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].totalRecords = total;
        },
        updateFilter: (
            state: TableState,
            action: PayloadAction<{ tableId?: string; filter: any }>
        ) => {
            const { tableId = "default", filter } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].filter = filter;
        },
        setTableLoading: (
            state: TableState,
            action: PayloadAction<{ tableId?: string; loading: boolean }>
        ) => {
            const { tableId = "default", loading } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].loading = loading;
        },
        updateRefreshKey: (
            state: TableState,
            action: PayloadAction<{ tableId?: string }>
        ) => {
            const { tableId = "default" } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].refreshKey = state[tableId].refreshKey + 1;
        },
        updateTotalData: <T = any>(
            state: TableState,
            action: PayloadAction<{ tableId?: string; data: T[] }>
        ) => {
            const { tableId = "default", data } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].totalData = data;
        },
        updateTableExtras: (
            state: TableState,
            action: PayloadAction<{
                tableId?: string;
                extras: { [key: string]: any };
            }>
        ) => {
            const { tableId = "default", extras } = action.payload;
            initializeTableState(state, tableId);
            state[tableId].extras = { ...state[tableId].extras, ...extras };
        },
    },
});

export const {
    updateSelectedRows,
    resetSelectedRows,
    updateTableData,
    updateTotalRecords,
    updateSelectedRowKeys,
    resetSelectedRowsKeys,
    updateFilter,
    setTableLoading,
    updateRefreshKey,
    updateTableExtras,
    updateTotalData,
    updateUnselectedRowsKeys,
    resetUnselectedRowsKeys,
    setAllSelected,
} = tableSlice.actions;

export default tableSlice.reducer;
